import { List as ImmutableList } from 'immutable';
import type { SagaIterator } from 'redux-saga';
import { call, put, race, select, take, takeEvery, takeLeading } from 'redux-saga/effects';
import { v4 as uuid } from 'uuid';

import { resourceSagaUtils, resourceStoreTypes } from 'app-components/flux/core';

import { getLogger, httpClientInterface, tracker } from '../../utils/core';
import { chartOfAccountsSelectors } from '../chart-of-accounts';
import { getStartOfMonthDate } from '../date/dateSelectors';
import { featureFlagsSelectors } from '../feature-flags';
import { filtersActions } from '../filters';
import { goalsActions } from '../goals';
import { scheduledTransactionsUtils } from '../scheduled-transactions';
import { transactionsActions } from '../transactions';
import * as actions from './freeToSpendActions';
import { getFreeToSpendById, getLastSyncDate } from './freeToSpendSelectors';
import {
  transformPastMonthRequestDataToFreeToSpend,
  transformRequestDataToFreeToSpend,
  transformResponseToFreeToSpend,
  transformRolloverRequestDataToFreeToSpend,
} from './freeToSpendTransformers';
import type { FreeToSpend, FreeToSpendPatch, FreeToSpendProps, PlannedSpendingItem } from './freeToSpendTypes';
import { mkFreeToSpend } from './freeToSpendTypes';

const log = getLogger('flux/free-to-spend');

const mkQcsSyncF2SConfig = resourceStoreTypes.createTypedQcsSyncResourcesConfigFactory<FreeToSpendProps>();

const mkResourceConfig = (props: any) =>
  mkQcsSyncF2SConfig({
    resourceName: 'free to spend',
    resourceBaseUrl: '/free-to-spend',
    getLastSyncDate,
    transformResourceToRequestData: transformRequestDataToFreeToSpend,
    transformResponseToResources: transformResponseToFreeToSpend,
    ...props,
  });

const { callFirst, callLast } = resourceSagaUtils.createGreedDebounce('freeToSpendDumpUpdater');
export function* freeToSpendDumpUpdater(resources: Array<FreeToSpend>) {
  yield callFirst(freeToSpendDumpUpdaterFull, resources);
  yield callLast(freeToSpendNudgeUpdater);
}

function* freeToSpendDumpUpdaterFull(resources: Array<FreeToSpend>): SagaIterator {
  const bumpSpendingPlan: boolean = yield select(featureFlagsSelectors.getFeatureFlag, 'bumpSpendingPlan');
  if (bumpSpendingPlan && resources) {
    const freeToSpends = resources.map((resource: FreeToSpend) => mkFreeToSpend(resource));
    yield put(actions.updateFreeToSpendsBump({ resources: freeToSpends }));
  }
}

export function* freeToSpendNudgeUpdater(): SagaIterator {
  const process: boolean = yield select(featureFlagsSelectors.getFeatureFlag, 'bumpSpendingPlan');
  if (process) {
    yield put(actions.getFreeToSpend());
  }
}

export function* getFreeToSpendActionWatcher() {
  const resourceConfig = mkResourceConfig({
    successAction: actions.getFreeToSpendSuccess,
    failureAction: actions.getFreeToSpendFailure,
  });
  yield resourceSagaUtils.takeAndDebounce(
    actions.getFreeToSpend,
    resourceSagaUtils.qcsSyncGetResources<FreeToSpendProps>,
    resourceConfig,
  );
}

function* fetchFreeToSpend(action: any) {
  yield put(actions.getFreeToSpend(null as any, { ...action.meta }));
}

function* triggerDeletePlannedItem(action: any) {
  try {
    // this delete request is using "POST" so it can't be done with qcsSyncDeleteResource
    const requestUrl = `${httpClientInterface.getServiceUrl()}/free-to-spend/planned-spending-item/delete/${
      action.payload.id
    }`;
    yield call(httpClientInterface.send, 'POST', requestUrl);
    if (action?.meta?.resolve && typeof action.meta.resolve == 'function') {
      yield call(action.meta.resolve);
    }
  } catch (error) {
    log.error('delete planned item failed with error ', error);
    if (action?.meta?.reject && typeof action.meta.reject == 'function') {
      yield call(action.meta.reject, error);
    }
  }
  yield put(actions.getFreeToSpend(null as any, {}));
}

export function* deletePlannedSpendingActionWatcher() {
  yield takeEvery(actions.deletePlannedSpending, triggerDeletePlannedItem);
}

export function* doBatchReleaseActionWatcher() {
  const resourceConfig = resourceStoreTypes.mkQcsSyncResourceConfig({
    resourceBaseUrl: (action) => `/free-to-spend/planned-spending/release-unused-funds?date=${action.payload}Z`,
    successAction: actions.getFreeToSpend,
    failureAction: undefined,
  });
  yield takeEvery(
    actions.doBatchRelease.type,
    resourceSagaUtils.qcsSyncCreateResource<resourceStoreTypes.BaseResource>,
    resourceConfig,
  );
}

export function* updateFreeToSpendActionWatcher() {
  const resourceConfig = mkResourceConfig({
    successAction: actions.updateFreeToSpendSuccess,
    failureAction: actions.updateFreeToSpendFailure,
  });
  const pastMonthConfig = mkResourceConfig({
    successAction: actions.updateFreeToSpendSuccess,
    failureAction: actions.updateFreeToSpendFailure,
    transformResourceToRequestData: transformPastMonthRequestDataToFreeToSpend,
  });
  const mkRolloverConfig = (filterId: string) =>
    mkResourceConfig({
      successAction: actions.updateFreeToSpendSuccess,
      failureAction: actions.updateFreeToSpendFailure,
      transformResourceToRequestData: (resource: FreeToSpend | FreeToSpendPatch) =>
        transformRolloverRequestDataToFreeToSpend(resource, filterId),
    });
  yield takeLeading(
    actions.updateFreeToSpend.type,
    function* sendUpdate(action: ReturnType<typeof actions.updateFreeToSpend>): SagaIterator<void> {
      const plan: FreeToSpend | undefined = yield select(getFreeToSpendById, action.payload.id ?? '');
      const startOfMonth: string = yield select(getStartOfMonthDate);
      const isPastMonth = !!plan?.date && plan.date < startOfMonth;
      let config = isPastMonth ? pastMonthConfig : resourceConfig;
      if (action.meta?.onlyRolloverOf) config = mkRolloverConfig(action.meta.onlyRolloverOf);
      yield call(resourceSagaUtils.qcsSyncUpdateResource<FreeToSpendProps>, config, action);
    },
  );
}

export function* upsertFreeToSpendFailureActionWatcher() {
  yield takeEvery(actions.updateFreeToSpendFailure, fetchFreeToSpend);
}

function* refreshAfterUpdate(action: ReturnType<typeof actions.updateFreeToSpendSuccess>): SagaIterator<void> {
  // A refresh sent now would read what the next write is about to change. If that write then finished first, its own
  // refresh would be dropped, because takeAndDebounce takes nothing while a fetch runs, and the old read would land last.
  if (!action.meta?.skipRefresh) yield call(fetchFreeToSpend, action);
}

export function* freeToSpendModifiedActionWatcher() {
  yield takeEvery(actions.updateFreeToSpendSuccess, refreshAfterUpdate);
}

export function* plannedSpendingActionWatcher() {
  yield takeEvery(actions.createPlannedSpending, freeToSpendCreatePlannedSpending);
}

// TODO: consolidate this with mobile and create implementation
// need to make and return a filter and its corresponding filter id
function* freeToSpendCreatePlannedSpending(action: ReturnType<typeof actions.createPlannedSpending>) {
  // Send the filterItems to the createFilter Saga and capture the returned filterID
  yield put(filtersActions.createFilter(action.payload.filterItems));
  const freeToSpend: FreeToSpend = yield select(getFreeToSpendById, action.payload?.id);
  const { filterSuccess } = yield race({
    filterSuccess: take(filtersActions.createFilterSuccess),
    filterFailure: take(filtersActions.createFilterFailure),
  });
  if (filterSuccess && freeToSpend) {
    const filterId = filterSuccess.payload?.id;
    const { targetAmount, name, recurring, rolloverType, resetRolloverAmount } = action.payload;
    const existingPlannedSpending = freeToSpend.plannedSpendingItems || ImmutableList<PlannedSpendingItem>();
    const payload = freeToSpend.merge({
      plannedSpendingItems: existingPlannedSpending.push({
        id: '',
        clientId: uuid(),
        name,
        targetAmount,
        filterId,
        recurring,
        rolloverType,
        resetRolloverAmount,
      }),
    });
    yield put(actions.updateFreeToSpend(payload));
  }
}
function* transactionModified(action: any) {
  const context = action?.meta?.context;
  if (
    context &&
    (context === 'Reg_Income' ||
      context === 'Reg_BillsAndSubscriptions' ||
      context === 'Reg_Savings' ||
      context === 'Reg_Spendings' ||
      context === 'Reg_FTS' ||
      context === 'Reg_FTS_2' ||
      context === 'addAccounts' ||
      context === 'spending-plan' ||
      context === 'spending-plan-goals' ||
      context === 'scheduledTransactions') &&
    (action.type !== transactionsActions.getTransactionsSuccess.toString() || action?.payload?.resources?.length > 0)
  ) {
    if (action.payload) {
      const resource =
        context === 'scheduledTransactions' && action.payload.resources ? action.payload.resources[0] : action.payload;
      const category = chartOfAccountsSelectors.getCoaStringSelector(null, resource.coa, true);
      const type = resource.isBill
        ? 'BILL'
        : resource.isSubscription
          ? 'SUBSCRIPTION'
          : scheduledTransactionsUtils.typeFromCOA(resource.coa ?? { id: '', type: 'NONE' });

      tracker.track(tracker.events.txnUpdatedInFTSRegister, {
        description: context,
        payee: resource.payee,
        category,
        type,
      });
    }

    yield put(actions.getFreeToSpend({} as any, { context }));
  }
}

function* transactionModifiedActionWatcher() {
  yield takeEvery(transactionsActions.getTransactionsSuccess, transactionModified);
  yield takeEvery(transactionsActions.updateTransactionSuccess, transactionModified);
  yield takeEvery(transactionsActions.performTransactionActionSuccess, transactionModified);
  yield takeEvery(transactionsActions.batchTransactionsSuccess, transactionModified);
  yield takeEvery(transactionsActions.deleteTransactionSuccess, transactionModified);
  yield takeEvery(transactionsActions.createTransactionSuccess, transactionModified);
}

/* Eventually QCS will detect goal changes automatically */
function* goalsModified(action: any) {
  if (action?.meta?.context === 'spending-plan' || action?.meta?.context === 'spending-plan-goals') {
    tracker.track(tracker.events.goalUpdatedInFTSRegister);
    yield put(actions.getFreeToSpend());
  }
}

function* goalsWatcher() {
  yield takeEvery(goalsActions.updateGoalSuccess, goalsModified);
  yield takeEvery(goalsActions.createGoalSuccess, goalsModified);
  yield takeEvery(goalsActions.deleteGoalSuccess, goalsModified);
}

export default [
  plannedSpendingActionWatcher,
  getFreeToSpendActionWatcher,
  updateFreeToSpendActionWatcher,
  upsertFreeToSpendFailureActionWatcher,
  freeToSpendModifiedActionWatcher,
  transactionModifiedActionWatcher,
  goalsWatcher,
  deletePlannedSpendingActionWatcher,
  doBatchReleaseActionWatcher,
];
